import { getMqttSource } from '@/lib/mqtt/source';
import type { RawMqttMessage } from '@/lib/mqtt/types';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useEffect } from 'react';

/** Subscribe to the raw MQTT tap while mounted and feed the store.
 *  Ingest is rAF-coalesced so a high-rate feed can't thrash React. */
export function useDataStreamFeed(): void {
  const wildcardOn = useDataStreamStore((s) => s.wildcardOn);

  useEffect(() => {
    const src = getMqttSource();
    let queue: RawMqttMessage[] = [];
    let frame = 0;

    const flush = () => {
      frame = 0;
      const batch = queue;
      queue = [];
      // One store commit for the whole frame's batch (not one per message), so
      // a 250 msg/s topic wakes subscribers ~60×/s instead of 250×/s.
      useDataStreamStore.getState().ingestBatch(batch);
    };

    const unsub = src.subscribeRaw((msg) => {
      queue.push(msg);
      if (frame === 0) frame = requestAnimationFrame(flush);
    });
    // ensure connected (idempotent)
    src.connect().catch(() => {
      /* connector already logs */
    });

    return () => {
      unsub();
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  // Apply the wildcard subscription on toggle.
  useEffect(() => {
    getMqttSource().setWildcard(wildcardOn);
    return () => getMqttSource().setWildcard(false);
  }, [wildcardOn]);
}
